import { useCallback, useEffect, useMemo, useState } from "react"
import { BSBuilding } from "../../../../../../../core/dto/beem-shot/BSBuilding/BSBuilding"
import { BSBuildingResult } from "../../../../../../../core/dto/beem-shot/BSBuildingResult/BSBuildingResult"
import { BSMaterialResultDashboard } from "../../../../../../../core/dto/beem-shot/BSMaterialResult/BSMaterialResultDashboard"
import { useBSBuildingResult } from "../../../../../../../core/hooks/beem-shot/useBSBuildingResult"
import { useBSBuilding } from "../../../../../../../core/hooks/beem-shot/useBSBuilding"
import { useBSMaterialResults } from "../../../../../../../core/hooks/beem-shot/useBSMaterialResults"
import { BSVariantDashboardDto } from "../../../../../../../core/dto/beem-shot/BSVariant/BSVariantDashboardDto"

export function useDashboardVariant(): DashboardVariantHook {
  const { getAllBSBuildingResult } = useBSBuildingResult()
  const { getAllBuilding } = useBSBuilding()
  const { getBSMaterialResultListDashboard } = useBSMaterialResults()

  const [bsVariant, setBSVariant] = useState<BSVariantDashboardDto | undefined>(undefined)
  const [bsBuilding, setBSBuilding] = useState<BSBuilding | undefined>(undefined)
  const [isBuildingLoading, setIsBuildingLoading] = useState<boolean>(false)
  const [bsBuildingResult, setBSBuildingResult] = useState<BSBuildingResult | undefined>(undefined)
  const [isBuildingResultLoading, setIsBuildingResultLoading] = useState<boolean>(false)
  const [bsMaterialResultList, setBSMaterialResultList] = useState<BSMaterialResultDashboard[]>([])

  const selectVariant: (variant: BSVariantDashboardDto) => void = useCallback((newVariant) => {
    setBSVariant(newVariant)
  }, [])

  useEffect(() => {
    if (bsVariant?.id) {
      setIsBuildingLoading(true)
      getAllBuilding(bsVariant.id)
        .then((response) => {
          if (response && response.length > 0) {
            setBSBuilding(response[0])
          } else {
            setBSBuilding(undefined)
          }
        })
        .finally(() => {
          setIsBuildingLoading(false)
        })
    } else {
      setBSBuildingResult(undefined)
    }
  }, [bsVariant?.id, getAllBuilding])

  useEffect(() => {
    if (bsVariant?.id) {
      setIsBuildingResultLoading(true)
      getAllBSBuildingResult(bsVariant.id)
        .then((response) => {
          if (response && response.length > 0) {
            setBSBuildingResult(response[0])
          } else {
            setBSBuildingResult(undefined)
          }
        })
        .finally(() => {
          setIsBuildingResultLoading(false)
        })
    } else {
      setBSBuildingResult(undefined)
    }
  }, [getAllBSBuildingResult, bsVariant?.id])

  useEffect(() => {
    if (bsVariant?.id && bsBuilding?.id) {
      getBSMaterialResultListDashboard(bsBuilding.id).then((response) => {
        setBSMaterialResultList(response)
      })
    } else {
      setBSMaterialResultList([])
    }
  }, [bsVariant?.id, bsBuilding?.id, getBSMaterialResultListDashboard])

  return useMemo(
    () => ({
      bsVariant,
      selectVariant,
      bsBuilding,
      isBuildingLoading,
      bsBuildingResult,
      isBuildingResultLoading,
      bsMaterialResultList,
    }),
    [
      bsVariant,
      selectVariant,
      bsBuilding,
      isBuildingLoading,
      bsBuildingResult,
      isBuildingResultLoading,
      bsMaterialResultList,
    ]
  )
}

interface DashboardVariantHook {
  bsVariant: BSVariantDashboardDto | undefined
  bsBuilding: BSBuilding | undefined
  isBuildingLoading: boolean
  bsBuildingResult: BSBuildingResult | undefined
  isBuildingResultLoading: boolean
  bsMaterialResultList: BSMaterialResultDashboard[]

  selectVariant(variant: BSVariantDashboardDto): void
}
